import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase";
import AppShell from "../components/layout/AppShell";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import AccountsPanel from "../components/expenses/AccountsPanel";
import ApiAccessPanel from "../components/settings/ApiAccessPanel";
import { apiFetch } from "../api/client";
import { getExpenses } from "../api/expenses";
import { useLiveRefresh } from "../utils/live";
import { timeAgo } from "../utils/contact";

// One place for how the dashboard is set up: what's connected, the expense
// accounts, and links to each area's own settings.

const PROJECT = "birdie-bus-app";
const SERVICE = "gcloud run services update birdie-bus-backend --project birdie-bus-app --region us-east1";

const SETUP = {
  resend: {
    steps: [
      "Sign up at resend.com → Domains → Add domain: updates.birdiebus.com",
      "Add the DNS records it shows, then wait for “Verified”",
      "API Keys → Create (Sending access), then run this in PowerShell:",
    ],
    command: `$key = Read-Host "Paste your Resend API key"\n${SERVICE} --update-env-vars "RESEND_API_KEY=$key"`,
  },
  scanning: {
    steps: ["Create a key at openrouter.ai → Keys, then run this in PowerShell:"],
    command: `$key = Read-Host "Paste your OpenRouter API key"\n${SERVICE} --update-env-vars "OPENROUTER_API_KEY=$key,OPENROUTER_MODEL=anthropic/claude-sonnet-5"`,
  },
  waivers: {
    steps: ["Run this once in PowerShell, then tap Check now on the Email page:"],
    command:
      "gcloud projects add-iam-policy-binding birdie-bus --member=serviceAccount:1076807640806-compute@developer.gserviceaccount.com --role=roles/datastore.viewer",
    link: { to: "/admin/email", label: "Open the Email page" },
  },
  alerts: { steps: ["Turn alerts on from the phone you want buzzed."], link: { to: "/admin/alerts", label: "Set up alerts" } },
  gmail: { steps: [`Set SMTP_SENDER_EMAIL and SMTP_APP_PASSWORD on the Cloud Run service (project ${PROJECT}).`] },
  square: { steps: [`Set SQUARE_ACCESS_TOKEN and SQUARE_LOCATION_ID on the Cloud Run service (project ${PROJECT}).`] },
  calendar: { steps: [`Set GOOGLE_CALENDAR_ID on the Cloud Run service (project ${PROJECT}).`] },
};

const LINKS = [
  { to: "/admin/email", label: "Email", sub: "Mailing address, sign-off, review link, automatic drafts, your list" },
  { to: "/admin/alerts", label: "Phone alerts", sub: "What buzzes your phone; sign in another device" },
  { to: "/admin/quote-page", label: "Quote page", sub: "Add-ons, the 8-hour upgrade, reviews and FAQ customers see" },
  { to: "/admin/services", label: "Services & packages", sub: "Package prices and add-ons" },
  { to: "/admin/numbers", label: "Costs & profit", sub: "Staff, mileage and fee assumptions on the Numbers page" },
];

export default function AdminSettings() {
  const [overview, setOverview] = useState(null);
  const [exp, setExp] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [o, e] = await Promise.all([apiFetch("/api/admin/settings/overview"), getExpenses().catch(() => null)]);
      setOverview(o);
      setExp(e);
    } catch (e) {
      setError(e.message || "Couldn't load settings.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh(load);
  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  async function logOut() {
    await signOut(auth);
    navigate("/admin/login", { replace: true });
  }

  return (
    <AppShell title="Settings">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && (
        <div className="mb-3 rounded-xl border border-birdie-green/25 bg-birdie-green/10 p-3 text-sm text-birdie-green">{notice}</div>
      )}
      {!overview ? (
        !error && (
          <div className="py-16 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : (
        <div className="space-y-4">
          <Section title="Connections" sub="What the dashboard is hooked up to.">
            <ul className="divide-y divide-birdie-green/10">
              {overview.connections.map((c) => (
                <Connection key={c.key} c={c} />
              ))}
            </ul>
          </Section>

          <Section title="Expenses" sub={`Partners: ${overview.partners.join(" and ")}.`}>
            {exp ? (
              <AccountsPanel data={exp} onChanged={load} onNotice={setNotice} onError={setError} />
            ) : (
              <p className="text-sm text-birdie-charcoal/55">Couldn't load accounts.</p>
            )}
          </Section>

          <Section
            title="API & MCP"
            sub="Let an AI assistant or another app build quotes for you. Anything it wants sent waits for your approval."
          >
            <ApiAccessPanel Command={Command} />
          </Section>

          <Section title="More settings">
            <ul className="divide-y divide-birdie-green/10 -mx-1">
              {LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="flex items-center justify-between gap-3 px-1 py-3 rounded-lg hover:bg-birdie-cream/60">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-birdie-charcoal">{l.label}</span>
                      <span className="block text-xs text-birdie-charcoal/55">{l.sub}</span>
                    </span>
                    <span className="text-birdie-charcoal/30 text-lg" aria-hidden>
                      ›
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Your account">
            <p className="text-sm text-birdie-charcoal/75">
              Signed in as <strong>{user?.email || "…"}</strong>
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Link
                to="/admin/alerts"
                className="inline-flex items-center rounded-xl border border-birdie-green/15 bg-white px-4 py-2 text-sm font-medium text-birdie-charcoal hover:bg-birdie-beige"
              >
                Sign in another device
              </Link>
              <Button variant="ghost" onClick={logOut} className="text-red-700">
                Sign out
              </Button>
            </div>
          </Section>
        </div>
      )}
    </AppShell>
  );
}

function Connection({ c }) {
  const [open, setOpen] = useState(false);
  const setup = SETUP[c.key];
  return (
    <li className="py-2.5">
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 h-5 w-5 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
            c.ok ? "bg-birdie-green/15 text-birdie-green" : "bg-amber-100 text-amber-700"
          }`}
          aria-label={c.ok ? "Connected" : "Needs setup"}
        >
          {c.ok ? "✓" : "!"}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-birdie-charcoal">{c.label}</div>
          <div className="text-xs text-birdie-charcoal/55">
            {c.detail}
            {c.at ? ` · checked ${timeAgo(c.at)}` : ""}
          </div>
        </div>
        {!c.ok && setup && (
          <button type="button" onClick={() => setOpen((v) => !v)} className="shrink-0 text-xs font-semibold text-birdie-green">
            {open ? "Hide" : "Set up"}
          </button>
        )}
      </div>
      {open && setup && (
        <div className="mt-2 ml-8 space-y-2 text-xs text-birdie-charcoal/75">
          <ol className="list-decimal pl-4 space-y-1">
            {setup.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          {setup.command && <Command text={setup.command} />}
          {setup.link && (
            <Link to={setup.link.to} className="inline-block font-semibold text-birdie-green">
              {setup.link.label} ›
            </Link>
          )}
        </div>
      )}
    </li>
  );
}

function Command({ text }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* it's selectable anyway */
    }
  }
  return (
    <div>
      <pre className="whitespace-pre-wrap break-all rounded-xl bg-birdie-charcoal text-white text-[11px] p-3 select-all">{text}</pre>
      <button type="button" onClick={copy} className="mt-1 text-xs font-semibold text-birdie-green">
        {copied ? "Copied ✓" : "Copy"}
      </button>
    </div>
  );
}

function Section({ title, sub, children }) {
  return (
    <section className="bg-white border border-birdie-green/12 rounded-2xl p-4">
      <h2 className="font-accent text-base text-birdie-charcoal">{title}</h2>
      {sub && <p className="text-xs text-birdie-charcoal/55 mt-0.5 mb-2">{sub}</p>}
      {!sub && <div className="mb-2" />}
      {children}
    </section>
  );
}
